import { GetScopeSearchRequest, GetScopeValuesRequest, ScopeItem, TopologyService } from "../../Scopes.types";
import { Enums } from "Shared/Utils";
import { ServiceEnvironment } from "Enums/ServiceEnvironment";
import { Constants } from "Shared/Constants";
import { useEnvironment } from "Shared/Hooks";

export interface EnvironmentCloudMap {
    /**
     * Name of the cloud
     */
    cloudName: string;
    /**
     * Used to define environment where this cloud name will be available.
     */
    envs?: string[];
}

export const supportedEnvironments: EnvironmentCloudMap[] = [
    { cloudName: "Prod", envs: Constants.prodAndAGOnlyEnvs },
    { cloudName: "Gallatin", envs: Constants.prodOnlyEnvs },
    { cloudName: "Itar", envs: Constants.prodOnlyEnvs },
    { cloudName: "Bleu", envs: Constants.sovereignBleuOnlyEnv },
    { cloudName: "Delos", envs: Constants.sovereignDelosOnlyEnv },
    { cloudName: "USNat", envs: ["AG08"] },
    { cloudName: "USSec", envs: ["AG09"] }
];

const allEnvironments = Enums.getNamesAndValuesOfEnum(ServiceEnvironment);
export const environmentRingMapping: Map<ServiceEnvironment, string[]> = new Map([
    [ServiceEnvironment.Prod, ["*", "BlackForest", "DONMT", "DSDFV2", "DSIP", "DWW", "MSIT", "PDT", "SDFV2", "SIP", "TDF", "WW"]],
    [ServiceEnvironment.Gallatin, ["*", "Gallatin", "GallatinSIP"]],
    [ServiceEnvironment.Itar, ["*", "DITARSIP", "ITARSIP", "ITAR", "DITAR"]],
    [ServiceEnvironment.Bleu, ["*", "BleuSC11WW", "BleuSC11SIP"]],
    [ServiceEnvironment.Delos, ["*", "DelosSC12SIP", "DelosSC12WW"]]
]);

const environmentValueToTextMapping: Map<ServiceEnvironment, string> = new Map(allEnvironments.map((env) => [env.value, env.name]));
const environmentTextToValueMapping: Map<string, ServiceEnvironment> = new Map(allEnvironments.map((env) => [env.name, env.value]));

export const getServiceEnvironmentFromText = (text: string) => environmentTextToValueMapping.get(text);
export const getServiceEnvironmentText = (env: ServiceEnvironment) => environmentValueToTextMapping.get(env);

export const getSelectedEnvironment = (scopes: ScopeItem[]): ServiceEnvironment | undefined => {
    const selectedEnv = scopes.find((scope) => scope.ScopeType?.toLowerCase() === "environment" || scope.ScopeType?.toLowerCase() === "exoenvironment");
    const serviceEnvironment = allEnvironments.find((val) => val.name.toLowerCase() === selectedEnv?.Value?.toLowerCase());
    return serviceEnvironment?.value;
};

export const useSubstrateEnvironmentRingTopologyService = (): TopologyService => {
    const { filterByEnvironment } = useEnvironment();
    return {
        getScopeValueOptions: (request: GetScopeValuesRequest): ScopeItem[] => {
            const currentScopeTarget = request.currentScopeTarget;
            switch (currentScopeTarget.ScopeType.toLowerCase()) {
                case "environment": {
                    const availableClouds = filterByEnvironment(supportedEnvironments);
                    return availableClouds.map((cloud) => ({ ScopeType: "Environment", Value: cloud.cloudName }));
                }
                case "ring":
                    const environment = getSelectedEnvironment(request.selectedScopesSoFar || []);
                    if (environment !== undefined) {
                        const availableRings = environmentRingMapping.get(environment) || [];
                        return availableRings.map((ring) => ({ ScopeType: "Ring", Value: ring }));
                    }
                    return [];
                default:
                    throw new Error("Only environment and ring scope types supported");
            }
        },
        getScopeSearch: (request: GetScopeSearchRequest): ScopeItem[] | Promise<ScopeItem[]> => [{ ScopeType: "", Value: "" }]
    };
};
